import jsPDF from 'jspdf';
import { STAGE_HINTS } from '../components/queen/QueenStageHints';

const STAGES = [
  { key: 'graft', day: 0, label: 'Graft', color: [37, 99, 235] },
  { key: 'finisher', day: 1, label: 'Finisher', color: [124, 58, 237] },
  { key: 'capped', day: 9, label: 'Capped', color: [147, 51, 234] },
  { key: 'transfer', day: 10, label: 'Transfer', color: [79, 70, 229] },
  { key: 'emerge', day: 16, label: 'Emerge', color: [219, 39, 119] },
  { key: 'mating', day: 22, label: 'Mating', color: [234, 88, 12] },
  { key: 'check', day: 30, label: 'Laying Check', color: [22, 163, 74] }
];
const AMBER = [180, 83, 9];
const DAY_MS = 86400000;
const parseDate = (s) => { const [y, m, d] = s.slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };
const fmt = (d, opts = { day: 'numeric', month: 'short' }) => d.toLocaleDateString('en-AU', opts);
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const drawPage = (doc, batch) => {
  const W = 297, M = 12;
  const graft = parseDate(batch.graft_date);

  // Header band
  const clip = (t, n = 32) => (t && t.length > n ? `${t.slice(0, n - 1)}…` : t);
  doc.setFontSize(8.5);
  const bandH = 24;
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, W, bandH, 'F');
  doc.setTextColor(255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(`Graft Calendar: ${clip(batch.batch_name, 60) || `Batch #${batch.batch_number || ''}`}`, M, 11);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const meta = [
    `Apiary: ${clip(batch.yard_name) || 'Not set'}`,
    `Breeder: ${clip(batch.breeder_queen_name) || '-'}`,
    `Cell Starter: ${batch.cell_builder_hive_number ? `Hive #${batch.cell_builder_hive_number}` : '-'}`,
    `Finisher: ${batch.finisher_hive_number ? `Hive #${batch.finisher_hive_number}` : 'Same as starter'}`,
    `Cells grafted: ${batch.cells_grafted || 0}`,
    `Grafted: ${fmt(graft, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}`
  ];
  const metaLines = doc.splitTextToSize(meta.join('   |   '), W - 2 * M);
  doc.setFillColor(...AMBER);
  if (metaLines.length > 1) doc.rect(0, bandH, W, 4, 'F');
  metaLines.slice(0, 2).forEach((l, i) => doc.text(l, M, 17 + i * 4.5));

  // Calendar grid (Mon-start weeks from graft to Day 30)
  const start = addDays(graft, -((graft.getDay() + 6) % 7));
  const end = addDays(graft, 30);
  const weeks = Math.ceil((Math.round((end - start) / DAY_MS) + 1) / 7);
  const gridTop = metaLines.length > 1 ? 32 : 29, headH = 6, rowH = weeks > 5 ? 10 : 12, colW = (W - 2 * M) / 7;
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  doc.setFillColor(254, 243, 199);
  doc.rect(M, gridTop, W - 2 * M, headH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(120, 53, 15);
  days.forEach((d, i) => doc.text(d, M + i * colW + colW / 2, gridTop + 4.2, { align: 'center' }));
  const stageByDay = Object.fromEntries(STAGES.map(s => [s.day, s]));
  for (let w = 0; w < weeks; w++) {
    for (let i = 0; i < 7; i++) {
      const date = addDays(start, w * 7 + i);
      const dayNum = Math.round((date - graft) / DAY_MS);
      const x = M + i * colW, y = gridTop + headH + w * rowH;
      const inRange = dayNum >= 0 && dayNum <= 30;
      const stage = inRange ? stageByDay[dayNum] : null;
      doc.setDrawColor(231, 229, 228);
      doc.setFillColor(...(stage ? [255, 251, 235] : inRange ? [255, 255, 255] : [250, 250, 249]));
      doc.rect(x, y, colW, rowH, 'FD');
      doc.setFont('helvetica', stage ? 'bold' : 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...(inRange ? [41, 37, 36] : [168, 162, 158]));
      doc.text(fmt(date), x + 1.5, y + 3.5);
      if (inRange) {
        doc.setFontSize(6);
        doc.setTextColor(168, 162, 158);
        doc.text(`Day ${dayNum}`, x + colW - 1.5, y + 3.5, { align: 'right' });
      }
      if (stage) {
        doc.setFillColor(...stage.color);
        doc.roundedRect(x + 1.5, y + rowH - 5.8, colW - 3, 4.6, 1.5, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(255);
        doc.text(stage.label, x + colW / 2, y + rowH - 2.6, { align: 'center' });
      }
    }
  }

  // Stage tip cards (4 x 2)
  const cardsTop = gridTop + headH + weeks * rowH + 5;
  const cardW = (W - 2 * M - 9) / 4, cardH = (210 - 10 - cardsTop - 3) / 2;
  const wrap = (text, width) => doc.splitTextToSize(text, width);
  STAGES.forEach((s, idx) => {
    const hint = STAGE_HINTS[s.key];
    const x = M + (idx % 4) * (cardW + 3), y = cardsTop + Math.floor(idx / 4) * (cardH + 3);
    doc.setDrawColor(...s.color);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'FD');
    doc.setFillColor(...s.color);
    doc.roundedRect(x, y, cardW, 6.5, 2, 2, 'F');
    doc.rect(x, y + 3, cardW, 3.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(255);
    doc.text(`${s.label} - Day ${s.day}`, x + 2, y + 4.5);
    doc.text(fmt(addDays(graft, s.day), { weekday: 'short', day: 'numeric', month: 'short' }), x + cardW - 2, y + 4.5, { align: 'right' });
    let ty = y + 9.5;
    const maxY = y + cardH - 1.5;
    const write = (text, color, style = 'normal', gap = 0) => {
      doc.setFont('helvetica', style);
      doc.setTextColor(...color);
      const lines = wrap(text, cardW - 4);
      lines.forEach((l, li) => {
        if (ty > maxY) return;
        const last = ty + 2.9 > maxY && li < lines.length - 1;
        doc.text(last ? `${l}…` : l, x + 2, ty);
        ty += last ? 99 : 2.9;
      });
      ty += gap;
    };
    doc.setFontSize(6.8);
    write(hint.summary, [87, 83, 78], 'italic', 0.8);
    hint.dos.slice(0, 2).forEach(d => write(`+ ${d}`, [21, 128, 61]));
    hint.donts.slice(0, 2).forEach(d => write(`x ${d}`, [185, 28, 28]));
    ty += 0.8;
    write(`Next: ${hint.next}`, [120, 53, 15], 'bold');
  });
  // 8th slot: notes box
  const nx = M + 3 * (cardW + 3), ny = cardsTop + cardH + 3;
  doc.setDrawColor(214, 211, 209);
  doc.setLineDashPattern([1, 1], 0);
  doc.roundedRect(nx, ny, cardW, cardH, 2, 2, 'S');
  doc.setLineDashPattern([], 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(120, 113, 108);
  doc.text('Notes', nx + 2, ny + 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.3);
  ['Accepted: ____ / ' + (batch.cells_grafted || 0), 'Capped: ____', 'Transferred: ____', 'Laying: ____'].forEach((l, i) => doc.text(l, nx + 2, ny + 10 + i * 5));

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(168, 162, 158);
  doc.text('Dates are worked out from the graft date. Weather and colony strength can shift them by a day or two.', M, 206);
  doc.text(`Printed ${fmt(new Date(), { day: 'numeric', month: 'short', year: 'numeric' })}`, W - M, 206, { align: 'right' });
};

export const buildGraftCalendar = (batches) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  batches.forEach((b, i) => {
    if (i > 0) doc.addPage('a4', 'landscape');
    drawPage(doc, b);
  });
  return doc;
};
